import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

const FB_KIND_LABEL = { idea: "Idea", bug: "Bug", praise: "Praise", other: "Other" };

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState(null);

  // Top-level tab: feedback, reports, or insights. (Submissions retired —
  // that flow was part of an earlier version and is no longer used.)
  const initialTopTab = (() => {
    const tab = new URLSearchParams(location.search).get("tab");
    if (tab === "feedback") return "feedback";
    if (tab === "reports") return "reports";
    if (tab === "insights") return "insights";
    return "insights";  // new default — the archivist's home is the dashboard
  })();
  const [topTab, setTopTab] = useState(initialTopTab);

  // Feedback state
  const [fbItems, setFbItems] = useState([]);
  const [fbFilter, setFbFilter] = useState("");

  // Reports state
  const [reports, setReports] = useState([]);
  const [reportFilter, setReportFilter] = useState("pending");

  // Insights state
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);

  const refreshFb = useCallback((status) => {
    const params = status ? { status } : {};
    api.get("/admin/feedback", { params })
      .then(({ data }) => setFbItems(data))
      .catch((err) => console.error("admin feedback failed", err));
  }, []);

  const refreshReports = useCallback((status) => {
    const params = status ? { status } : {};
    api.get("/admin/comments/reports", { params })
      .then(({ data }) => setReports(data))
      .catch((err) => console.error("admin reports failed", err));
  }, []);

  const refreshInsights = useCallback(() => {
    setInsightsLoading(true);
    api.get("/admin/insights")
      .then(({ data }) => setInsights(data))
      .catch((err) => { console.error("admin insights failed", err); toast.error("Could not load insights."); })
      .finally(() => setInsightsLoading(false));
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    api.get("/auth/is-admin")
      .then(({ data }) => {
        setIsAdmin(data.is_admin);
        if (data.is_admin) {
          if (topTab === "feedback") refreshFb(fbFilter);
          else if (topTab === "reports") refreshReports(reportFilter);
          else if (topTab === "insights") refreshInsights();
        }
      })
      .catch((err) => { console.error("is-admin check failed", err); setIsAdmin(false); });
  }, [user, loading, topTab, fbFilter, reportFilter, refreshFb, refreshReports, refreshInsights, navigate]);

  const markFb = async (id, status) => {
    try {
      await api.patch(`/admin/feedback/${id}`, { status });
      refreshFb(fbFilter);
    } catch { toast.error("Could not update."); }
  };

  const moderateComment = async (commentId, action) => {
    try {
      await api.patch(`/admin/comments/${commentId}`, { action });
      refreshReports(reportFilter);
      toast.success(action === "remove" ? "Comment removed." : action === "restore" ? "Comment restored." : "Report dismissed.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update.");
    }
  };

  if (loading || isAdmin === null) {
    return <div className="max-w-3xl mx-auto px-6 py-32 text-center text-muted-foreground/75 italic serif text-xl">Verifying…</div>;
  }
  if (!isAdmin) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="admin-denied">
        <div className="label mb-4">Sealed</div>
        <h1 className="serif text-4xl text-foreground">This passage is for archivists.</h1>
        <p className="mt-6 text-muted-foreground">If you should have access, contact the steward.</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="max-w-5xl mx-auto px-6 sm:px-10 py-20">
      <div className="label mb-6 text-[hsl(var(--accent))]">Archivists</div>
      <h1 className="serif text-5xl text-foreground">The moderation room.</h1>

      {/* Top-level tabs */}
      <div className="mt-10 flex gap-8 hairline-b pb-4">
        <button
          onClick={() => setTopTab("insights")}
          data-testid="admin-top-insights"
          className={`mono text-[11px] uppercase tracking-[0.22em] link-underline ${topTab === "insights" ? "text-[hsl(var(--primary))]" : "text-muted-foreground"}`}
        >
          Insights
        </button>
        <button
          onClick={() => setTopTab("feedback")}
          data-testid="admin-top-feedback"
          className={`mono text-[11px] uppercase tracking-[0.22em] link-underline ${topTab === "feedback" ? "text-[hsl(var(--primary))]" : "text-muted-foreground"}`}
        >
          Feedback inbox
        </button>
        <button
          onClick={() => setTopTab("reports")}
          data-testid="admin-top-reports"
          className={`mono text-[11px] uppercase tracking-[0.22em] link-underline ${topTab === "reports" ? "text-[hsl(var(--primary))]" : "text-muted-foreground"}`}
        >
          Reports
        </button>
      </div>

      {topTab === "feedback" && (
        <>
          <div className="mt-8 flex gap-6 hairline-b pb-4">
            {["", "new", "read", "archived"].map((s) => (
              <button
                key={s || "all"}
                onClick={() => setFbFilter(s)}
                data-testid={`admin-fb-filter-${s || "all"}`}
                className={`mono text-[11px] uppercase tracking-[0.22em] link-underline ${fbFilter === s ? "text-[hsl(var(--primary))]" : "text-muted-foreground"}`}
              >
                {s ? s : "All"}
              </button>
            ))}
          </div>

          <div className="mt-10 space-y-px bg-border" data-testid="admin-feedback-list">
            {fbItems.length === 0 && (
              <div className="bg-card p-12 text-center text-muted-foreground/75 italic serif text-lg">No feedback yet.</div>
            )}
            {fbItems.map((f) => (
              <div key={f.id} data-testid={`admin-fb-${f.id}`} className="bg-card p-8">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span className="mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--primary))]">
                      {FB_KIND_LABEL[f.kind] || f.kind}
                    </span>
                    <span className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground/75">
                      {f.status}
                    </span>
                  </div>
                  <span className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground/75">
                    {f.user_name} · {new Date(f.created_at).toLocaleString()}
                  </span>
                </div>
                <p className="serif text-lg text-foreground leading-relaxed whitespace-pre-wrap">{f.message}</p>
                <p className="mt-2 mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground/75">{f.user_email}</p>
                <div className="mt-5 flex gap-4">
                  {f.status !== "read" && (
                    <button onClick={() => markFb(f.id, "read")} data-testid={`admin-fb-read-${f.id}`} className="btn-ghost">Mark read</button>
                  )}
                  {f.status !== "archived" && (
                    <button onClick={() => markFb(f.id, "archived")} data-testid={`admin-fb-archive-${f.id}`} className="btn-ghost">Archive</button>
                  )}
                  {f.status !== "new" && (
                    <button onClick={() => markFb(f.id, "new")} data-testid={`admin-fb-new-${f.id}`} className="btn-ghost">Reopen</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {topTab === "reports" && (
        <>
          <div className="mt-8 flex gap-6 hairline-b pb-4">
            {["pending", "removed", "restored", "dismissed", ""].map((s) => (
              <button
                key={s || "all"}
                onClick={() => setReportFilter(s)}
                data-testid={`admin-report-filter-${s || "all"}`}
                className={`mono text-[11px] uppercase tracking-[0.22em] link-underline ${reportFilter === s ? "text-[hsl(var(--primary))]" : "text-muted-foreground"}`}
              >
                {s || "All"}
              </button>
            ))}
          </div>
          <div className="mt-10 space-y-px bg-border" data-testid="admin-reports-list">
            {reports.length === 0 && (
              <div className="bg-card p-12 text-center text-muted-foreground/75 italic serif text-lg">No reports here.</div>
            )}
            {reports.map((r) => (
              <div key={`${r.comment_id}_${r.reporter_id}`} data-testid={`admin-report-${r.comment_id}`} className="bg-card p-8">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
                  <div className="mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--destructive))]">
                    Reported · {r.status}
                  </div>
                  <span className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground/75">
                    by {r.reporter_name} · {new Date(r.created_at).toLocaleString()}
                  </span>
                </div>
                {r.reason && <p className="serif italic text-muted-foreground mb-3">Reason: {r.reason}</p>}
                <div className="paper p-4 mt-2">
                  <div className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">
                    Comment by {r.comment?.user_name || "unknown"}
                    {r.comment?.user_handle && <> · @{r.comment.user_handle}</>}
                  </div>
                  <p className="text-foreground/90 leading-relaxed whitespace-pre-wrap">
                    {r.comment?.content || "[comment missing]"}
                  </p>
                  <Link
                    to={`/concept/${r.concept_id}`}
                    className="mt-3 inline-block mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground link-underline"
                  >
                    View in context →
                  </Link>
                </div>
                {r.status === "pending" && (
                  <div className="mt-5 flex gap-4">
                    <button
                      data-testid={`admin-report-remove-${r.comment_id}`}
                      onClick={() => moderateComment(r.comment_id, "remove")}
                      className="btn-gold"
                    >
                      Remove comment
                    </button>
                    <button
                      data-testid={`admin-report-dismiss-${r.comment_id}`}
                      onClick={() => moderateComment(r.comment_id, "dismiss")}
                      className="btn-ghost"
                    >
                      Dismiss
                    </button>
                  </div>
                )}
                {r.status === "removed" && (
                  <div className="mt-5 flex gap-4">
                    <button
                      data-testid={`admin-report-restore-${r.comment_id}`}
                      onClick={() => moderateComment(r.comment_id, "restore")}
                      className="btn-ghost"
                    >
                      Restore
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {topTab === "insights" && (
        <InsightsTab data={insights} loading={insightsLoading} onRefresh={refreshInsights} />
      )}
    </div>
  );
}

// ============================================================
// Insights — founder dashboard (growth / engagement / queues).
// Reads /api/admin/insights on demand. No new client state,
// no polling — the "Refresh" button re-fetches when needed.
// ============================================================
function InsightsTab({ data, loading, onRefresh }) {
  if (loading && !data) {
    return (
      <div data-testid="insights-loading" className="mt-16 text-center text-muted-foreground/75 italic serif text-lg">
        Reading the ledger…
      </div>
    );
  }
  if (!data) {
    return (
      <div className="mt-16 text-center text-muted-foreground/75 italic serif text-lg">
        No data yet.{" "}
        <button onClick={onRefresh} className="link-underline text-[hsl(var(--primary))] mono text-[11px] uppercase tracking-[0.2em] ml-2">
          Try again
        </button>
      </div>
    );
  }

  const { growth, engagement, queues, generated_at } = data;
  const seriesMax = Math.max(1, ...(growth.signup_series_30d || []).map((r) => r.count));

  return (
    <div data-testid="insights-tab" className="mt-10 space-y-12">
      <div className="flex items-baseline justify-between">
        <p className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          Generated {new Date(generated_at).toLocaleString()}
        </p>
        <button
          onClick={onRefresh}
          data-testid="insights-refresh"
          className="mono text-[10px] uppercase tracking-[0.2em] link-underline text-[hsl(var(--primary))]"
        >
          Refresh →
        </button>
      </div>

      {/* GROWTH */}
      <section className="paper p-8" data-testid="insights-growth">
        <div className="label mb-6">Growth</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
          <Stat label="Total seekers" value={growth.total_users} />
          <Stat label="New · 7d" value={growth.new_users_7d} />
          <Stat label="New · 30d" value={growth.new_users_30d} />
          <Stat label="Google · JWT" value={`${growth.auth_breakdown.google} · ${growth.auth_breakdown.jwt}`} />
        </div>
        <div className="grid grid-cols-3 gap-6 mt-6">
          <Stat label="Weekly digest opt-in" value={growth.digest_opt_in} sub={growth.total_users ? `${Math.round((growth.digest_opt_in / growth.total_users) * 100)}%` : "—"} />
          <Stat label="Push subscribers" value={growth.push_subscribers} sub={growth.total_users ? `${Math.round((growth.push_subscribers / growth.total_users) * 100)}%` : "—"} />
          <Stat label="Public profiles" value={growth.public_profiles} sub={growth.total_users ? `${Math.round((growth.public_profiles / growth.total_users) * 100)}%` : "—"} />
        </div>

        {/* Signup mini-chart. Simple flexed bars — no chart library. */}
        <div className="mt-10">
          <div className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-4">
            Signups by day · last 30d
          </div>
          {growth.signup_series_30d && growth.signup_series_30d.length > 0 ? (
            <div className="flex items-end gap-1 h-24" data-testid="insights-signup-chart">
              {growth.signup_series_30d.map((r) => (
                <div key={r.day} className="flex-1 flex flex-col items-center min-w-0" title={`${r.day}: ${r.count}`}>
                  <div
                    className="w-full bg-[hsl(var(--primary))] transition-all"
                    style={{ height: `${Math.max(6, (r.count / seriesMax) * 100)}%` }}
                  />
                  <span className="mono text-[9px] text-muted-foreground/70 mt-1 truncate w-full text-center">
                    {r.day.slice(5)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="serif italic text-muted-foreground/75">No signups in the last 30 days.</p>
          )}
        </div>
      </section>

      {/* ENGAGEMENT */}
      <section className="paper p-8" data-testid="insights-engagement">
        <div className="label mb-6">Engagement</div>
        <div className="grid grid-cols-3 gap-6">
          <Stat label="DAU · 24h" value={engagement.dau} />
          <Stat label="WAU · 7d"  value={engagement.wau} />
          <Stat label="MAU · 30d" value={engagement.mau} />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 mt-6">
          <Stat label="Oracle sessions · 7d"  value={engagement.oracle_sessions_7d} />
          <Stat label="Public posts · 7d"     value={engagement.public_posts_7d} sub={`${engagement.public_posts_30d} in 30d`} />
          <Stat label="Comments · 7d"         value={engagement.comments_7d} />
          <Stat label="Direct messages · 7d" value={engagement.dms_7d} />
          <Stat label="Submissions · 7d"      value={engagement.submissions_7d} />
        </div>

        {/* Top traditions viewed */}
        <div className="mt-10">
          <div className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-4">
            Most-read traditions · 7d
          </div>
          {engagement.top_traditions_7d && engagement.top_traditions_7d.length > 0 ? (
            <ul className="space-y-2" data-testid="insights-top-traditions">
              {engagement.top_traditions_7d.map((t) => (
                <li key={t.correlation_id} className="flex items-center justify-between hairline-b py-2 last:border-b-0">
                  <span className="serif text-foreground/90 truncate mr-4">{t.correlation_id}</span>
                  <span className="mono text-[11px] text-muted-foreground whitespace-nowrap">
                    {t.views} views · {t.readers} readers
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="serif italic text-muted-foreground/75">Nothing read yet this week.</p>
          )}
        </div>

        {/* Top active users */}
        <div className="mt-10">
          <div className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-4">
            Most active seekers · 7d
          </div>
          {engagement.top_active_users_7d && engagement.top_active_users_7d.length > 0 ? (
            <ul className="space-y-2" data-testid="insights-top-active">
              {engagement.top_active_users_7d.map((u) => (
                <li key={u.user_id} className="flex items-center justify-between hairline-b py-2 last:border-b-0">
                  <span className="serif text-foreground/90 truncate mr-4">
                    {u.handle ? (
                      <Link to={`/u/${u.handle}`} className="link-underline">{u.name} <span className="mono text-[10px] text-muted-foreground">@{u.handle}</span></Link>
                    ) : (
                      <>{u.name} <span className="mono text-[10px] text-muted-foreground/60">(no handle)</span></>
                    )}
                  </span>
                  <span className="mono text-[11px] text-muted-foreground whitespace-nowrap">
                    {u.actions} actions
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="serif italic text-muted-foreground/75">No standout activity this week.</p>
          )}
        </div>
      </section>

      {/* QUEUES */}
      <section className="paper p-8" data-testid="insights-queues">
        <div className="label mb-6">Queues waiting on you</div>
        <div className="grid grid-cols-3 gap-6">
          <Stat label="Submissions pending" value={queues.submissions_pending} highlight={queues.submissions_pending > 0} />
          <Stat label="Reports pending"     value={queues.reports_pending}     highlight={queues.reports_pending > 0} />
          <Stat label="Feedback new"        value={queues.feedback_new}        highlight={queues.feedback_new > 0} />
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, sub, highlight }) {
  return (
    <div>
      <div className={`serif text-4xl ${highlight ? "text-[hsl(var(--primary))]" : "text-foreground"}`}>
        {value}
      </div>
      <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">
        {label}
      </div>
      {sub && (
        <div className="mono text-[10px] text-muted-foreground/70 mt-0.5">{sub}</div>
      )}
    </div>
  );
}

